import styles from "./StockRemovalReview.module.scss";
import secondaryComponents from "../../../../../components/secondaryComponents";
import primaryComponents from "../../../../../components/primaryComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useStockRemovalReview from "./useStockRemovalReview";
import globalStyles from "../../../../../assets/styles/base/globalPage.module.scss";
import StockRemovalReviewActions from "./StockRemovalReviewActions";
import StockRemovalDetailsCard from "./StockRemovalDetailsCard";

const StockRemovalReview = () => {
  const {
    id,
    removalDetails,
    isLoadingDetails,
    isApproving,
    isRejecting,
    isSubmittingDraft,
    isDeleting,
    isApproveModalOpen,
    setIsApproveModalOpen,
    isRejectModalOpen,
    setIsRejectModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isEditDraftModalOpen,
    setIsEditDraftModalOpen,
    previewImage,
    setPreviewImage,
    handleApprove,
    handleReject,
    handleSubmitDraft,
    handleDelete,
    handleBack,
    refetchDetails,
  } = useStockRemovalReview();

  const status = removalDetails?.status || "REVIEW";
  const isPending =
    status === "REVIEW" || status === "IN_REVIEW" || status === "PENDING";
  const isApproved = status === "APPROVED";
  const isRejected = status === "REJECTED";
  const isDraft = status === "DRAFT";

  const statusClass = isPending
    ? globalStyles["statusBlock--pending"]
    : isApproved
      ? globalStyles["statusBlock--success"]
      : isRejected
        ? globalStyles["statusBlock--failed"]
        : globalStyles.statusBlock;

  const statusDisplay = isPending
    ? "In Review"
    : isApproved
      ? "Approved"
      : isRejected
        ? "Rejected"
        : isDraft
          ? "Draft"
          : status;

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Stock Removal Review"
      description="Review stock removal request"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <div className={styles.stockRemovalReview}>
          <h3>Inventory</h3>

          <div className={styles.stockRemovalReview__header}>
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: "16px" }}
            >
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={handleBack}
                type="button"
                style={{ width: "max-content" }}
              >
                <FontAwesomeIcon
                  icon="angle-left"
                  className="mr-2"
                  style={{ fontSize: "16px" }}
                />
                <span>Back</span>
              </primaryComponents.Button>

              <h4 className="mb-0">
                Stock Removal Request:{" "}
                <span style={{ color: "#7647EE" }}>
                  {removalDetails?.request_id || id}
                </span>
              </h4>

              <span className={statusClass}>{statusDisplay}</span>
            </div>

            {/* Actions Bar */}
            <StockRemovalReviewActions
              isPending={isPending}
              isDraft={isDraft}
              isApproving={isApproving}
              isRejecting={isRejecting}
              isDeleting={isDeleting}
              isSubmittingDraft={isSubmittingDraft}
              setIsApproveModalOpen={setIsApproveModalOpen}
              setIsRejectModalOpen={setIsRejectModalOpen}
              setIsDeleteModalOpen={setIsDeleteModalOpen}
              setIsEditDraftModalOpen={setIsEditDraftModalOpen}
              handleSubmitDraft={handleSubmitDraft}
            />
          </div>

          {/* Rejection notice banner if rejected */}
          {isRejected && removalDetails?.rejection_reason && (
            <div className={styles.stockRemovalReview__rejectionNotice}>
              <h6>Rejection Reason</h6>
              <p>{removalDetails.rejection_reason}</p>
            </div>
          )}

          {/* Details Card */}
          <div>
            <h5>Request Details</h5>
            <secondaryComponents.LoaderHelper
              isLoading={isLoadingDetails}
              classNames="mt-3"
            >
              <StockRemovalDetailsCard removalDetails={removalDetails} />
            </secondaryComponents.LoaderHelper>
          </div>

          {/* Items Section */}
          <div>
            <h5>Removal Items & Evidence</h5>
            <secondaryComponents.LoaderHelper
              isLoading={isLoadingDetails}
              classNames="mt-3"
            >
              <div className={styles.stockRemovalReview__tableWrapper}>
                <secondaryComponents.StockRemovalItemsTable
                  items={removalDetails?.items}
                  setPreviewImage={setPreviewImage}
                />
              </div>
            </secondaryComponents.LoaderHelper>
          </div>
        </div>

        {/* Approve Confirmation Modal */}
        <secondaryComponents.ConfirmDialog
          isOpen={isApproveModalOpen}
          setIsOpen={setIsApproveModalOpen}
          title="Approve Stock Removal"
          confirmText="Confirm Approval"
          btnClassNames="btn btn--primary cover"
          cancelBtnClassNames="btn btn--outline cover"
          loadingText="Approving..."
          handler={handleApprove}
          isLoading={isApproving}
          width="480px"
        >
          <p
            style={{
              color: "#718096",
              textAlign: "center",
              margin: 0,
              lineHeight: "1.6",
            }}
          >
            Are you sure you want to approve this stock removal request? Once
            approved, the stock quantities will be permanently deducted and
            recorded in stock history.
          </p>
        </secondaryComponents.ConfirmDialog>

        {/* Reject Modal with Reason Input */}
        <secondaryComponents.RejectRequestModal
          isActive={isRejectModalOpen}
          closeModal={() => setIsRejectModalOpen(false)}
          title="Reject Stock Removal"
          description="Please provide a reason for rejecting this removal request. The stock will remain untouched."
          placeholder="Enter rejection reason..."
          submitBtnText="Reject Request"
          onConfirm={handleReject}
          isLoading={isRejecting}
        />

        {/* Delete Confirmation Modal */}
        <secondaryComponents.ConfirmDialog
          title="Delete Removal Request"
          isOpen={isDeleteModalOpen}
          setIsOpen={setIsDeleteModalOpen}
          handler={handleDelete}
          isLoading={isDeleting}
        >
          <p style={{ color: "#718096", textAlign: "center" }} className="mb-0">
            Are you sure you want to delete this removal request? This action
            cannot be undone.
          </p>
        </secondaryComponents.ConfirmDialog>

        {/* Full Image Preview Modal */}
        <secondaryComponents.Modal
          isActive={Boolean(previewImage)}
          closeModal={() => setPreviewImage(null)}
          width="600px"
        >
          <div style={{ textAlign: "center", padding: "16px" }}>
            <h5 className="mb-3">Evidence Photo</h5>
            <img
              src={previewImage}
              alt="Evidence full preview"
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </div>
        </secondaryComponents.Modal>

        {/* Edit Draft Sidebar Modal */}
        <secondaryComponents.SideModal
          width={650}
          isActive={isEditDraftModalOpen}
          closeModal={() => setIsEditDraftModalOpen(false)}
        >
          <secondaryComponents.DeleteStockWidget
            draftRequest={removalDetails}
            closeWidget={() => {
              setIsEditDraftModalOpen(false);
              refetchDetails();
            }}
          />
        </secondaryComponents.SideModal>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};

export default StockRemovalReview;
